/**
 * Practice Tests Service
 * Handles all practice test-related database operations
 * Updated to use separated tables for each section
 */

import { supabase } from './supabase.service';
import logger from '../logging/logger';
import errorTracker from '../logging/errorTracker';

const PracticeTestsService = {
  /**
   * Get table names for a specific section
   * @param {string} section - Section name
   * @returns {Object} Table names for questions and passages
   */
  _getTableNames(section) {
    const tableMap = {
      english: {
        questions: 'practice_test_english_questions',
        passages: 'practice_test_english_passages'
      },
      math: {
        questions: 'practice_test_math_questions',
        passages: null
      },
      reading: {
        questions: 'practice_test_reading_questions',
        passages: 'practice_test_reading_passages'
      },
      science: {
        questions: 'practice_test_science_questions',
        passages: 'practice_test_science_passages'
      }
    };
    return tableMap[section] || null;
  },

  /**
   * Fetch questions for a specific section with passages
   * @param {number} testNumber - Test number
   * @param {string} section - Section name
   * @returns {Promise<Array|null>} Questions with passage data
   */
  async getPracticeTestSection(testNumber, section) {
    logger.debug('PracticeTestsService', 'getPracticeTestSection', { testNumber, section });

    const tables = this._getTableNames(section);
    if (!tables) {
      errorTracker.trackError(
        'PracticeTestsService',
        'getPracticeTestSection',
        { testNumber, section },
        new Error(`Invalid section: ${section}`)
      );
      return null;
    }

    console.log(`🗄️ Querying ${tables.questions} for test_number=${testNumber}`);

    try {
      // Fetch questions
      const { data: questions, error: questionsError } = await supabase
        .from(tables.questions)
        .select('*')
        .eq('test_number', testNumber)
        .order('question_number', { ascending: true });

      console.log(`📦 Got ${questions?.length || 0} questions from ${tables.questions}`);

      if (questionsError) {
        errorTracker.trackError(
          'PracticeTestsService',
          'getPracticeTestSection',
          { testNumber, section },
          questionsError
        );
        return null;
      }

      // If section has passages, fetch and merge them
      if (tables.passages) {
        const { data: passages, error: passagesError } = await supabase
          .from(tables.passages)
          .select('*')
          .eq('test_number', testNumber)
          .order('passage_number', { ascending: true });

        if (passagesError) {
          errorTracker.trackError(
            'PracticeTestsService',
            'getPracticeTestSection',
            { testNumber, section },
            passagesError
          );
          return null;
        }

        // Create passage lookup map
        const passageMap = {};
        passages.forEach((passage) => {
          passageMap[passage.id] = passage;
        });

        // Merge passage text into questions
        questions.forEach((question) => {
          if (question.passage_id && passageMap[question.passage_id]) {
            const passage = passageMap[question.passage_id];
            question.passage = passage.passage_text;
            question.passage_type = passage.passage_type;
            question.passage_title = passage.passage_title;
            question.passage_number = passage.passage_number; // Add passage_number for correct ordering

            // Collect all image URLs from image_url_1, image_url_2, etc.
            const imageUrls = {};
            for (let i = 1; i <= 5; i++) {
              const urlKey = `image_url_${i}`;
              if (passage[urlKey]) {
                imageUrls[`image${i}`] = passage[urlKey];
              }
            }
            if (Object.keys(imageUrls).length > 0) {
              question.passage_image_urls = imageUrls;
            }
          }
        });
      }

      // Transform questions to match SequentialTest expected format
      const transformedQuestions = questions.map((q) => {
        const parsedChoices = typeof q.choices === 'string' ? JSON.parse(q.choices) : q.choices;

        // Convert choices array to answers object for InteractiveQuestion
        // ["A. Text", "B. Text"] => {A: "Text", B: "Text"}
        // Also supports F-K for math questions: ["F. Text", "G. Text", "H. Text", "J. Text", "K. Text"]
        const answers = {};
        parsedChoices.forEach(choice => {
          const match = choice.match(/^([A-K])\.\s*(.+)$/);
          if (match) {
            answers[match[1]] = match[2];
          }
        });

        // Handle correct_answer in multiple formats:
        // - If already a letter ("A", "B", etc.), use as-is
        // - If a number string ("0", "1", "2", etc.), convert to letter
        // - If "TBD" or invalid, use "A" as placeholder (will be wrong but allows rendering)
        let correctAnswer = 'A'; // Default placeholder
        if (q.correct_answer) {
          const answer = String(q.correct_answer).trim();

          // Check if already a letter (A-K)
          if (/^[A-K]$/i.test(answer)) {
            correctAnswer = answer.toUpperCase();
          }
          // Check if a number (0-4 for A-E, 0-5 for F-K in math)
          else if (/^\d+$/.test(answer)) {
            const numAnswer = parseInt(answer, 10);
            correctAnswer = String.fromCharCode(65 + numAnswer); // 0->A, 1->B, etc.
          }
          // If contains "TBD" or other invalid format, use default 'A'
          else if (answer.includes('TBD')) {
            console.warn(`Question ${q.question_number} has placeholder answer "TBD", using 'A' as default`);
            correctAnswer = 'A';
          }
        }

        // Skip only if question_text is completely missing (not if it has TBD)
        if (!q.question_text || q.question_text.trim().length === 0) {
          console.warn(`Question ${q.question_number} has no question_text, skipping`);
          return null;
        }

        return {
          id: q.id,
          question_number: q.question_number, // Include question number for review mode
          text: q.question_text, // SequentialTest expects 'text'
          passage: q.passage,
          passage_title: q.passage_title, // Include passage title
          passage_image_urls: q.passage_image_urls, // Include passage image URLs for placeholder replacement
          answers: answers, // SequentialTest expects 'answers' as object
          correctAnswer: correctAnswer,
          explanation: q.explanation,
          question_type: q.question_type,
          difficulty: q.difficulty,
          image_url: q.image_url // Include image URL for questions with diagrams
        };
      }).filter(q => q !== null); // Remove skipped questions

      logger.info('PracticeTestsService', 'getPracticeTestSection', {
        testNumber,
        section,
        count: transformedQuestions.length
      });

      return transformedQuestions;
    } catch (error) {
      errorTracker.trackError(
        'PracticeTestsService',
        'getPracticeTestSection',
        { testNumber, section },
        error
      );
      return null;
    }
  },

  /**
   * Fetch all questions for a test (all sections)
   * @param {number} testNumber - Test number
   * @returns {Promise<Array|null>} All questions from all sections
   */
  async getPracticeTestQuestions(testNumber) {
    logger.debug('PracticeTestsService', 'getPracticeTestQuestions', { testNumber });

    try {
      const allQuestions = [];
      const sections = ['english', 'math', 'reading', 'science'];

      for (const section of sections) {
        const sectionQuestions = await this.getPracticeTestSection(testNumber, section);
        if (sectionQuestions) {
          // Add section identifier to each question
          sectionQuestions.forEach((q) => {
            q.section = section;
          });
          allQuestions.push(...sectionQuestions);
        }
      }

      logger.info('PracticeTestsService', 'getPracticeTestQuestions', {
        testNumber,
        totalCount: allQuestions.length
      });

      return allQuestions.length > 0 ? allQuestions : null;
    } catch (error) {
      errorTracker.trackError(
        'PracticeTestsService',
        'getPracticeTestQuestions',
        { testNumber },
        error
      );
      return null;
    }
  },

  /**
   * Get test structure (question counts by section)
   * @param {number} testNumber - Test number
   * @returns {Promise<Object|null>} Object with counts by section
   */
  async getTestStructure(testNumber) {
    logger.debug('PracticeTestsService', 'getTestStructure', { testNumber });

    try {
      const structure = {};
      const sections = ['english', 'math', 'reading', 'science'];

      for (const section of sections) {
        const tables = this._getTableNames(section);
        if (!tables) continue;

        const { count, error } = await supabase
          .from(tables.questions)
          .select('*', { count: 'exact', head: true })
          .eq('test_number', testNumber);

        if (error) {
          console.error(`Error getting count for ${section}:`, error);
          continue;
        }

        structure[section] = count || 0;
      }

      logger.info('PracticeTestsService', 'getTestStructure', {
        testNumber,
        structure
      });

      return structure;
    } catch (error) {
      errorTracker.trackError('PracticeTestsService', 'getTestStructure', { testNumber }, error);
      return null;
    }
  },

  /**
   * Get passages for a specific section
   * @param {number} testNumber - Test number
   * @param {string} section - Section name ('english', 'reading', 'science')
   * @returns {Promise<Array|null>} Array of passages
   */
  async getPassages(testNumber, section) {
    logger.debug('PracticeTestsService', 'getPassages', { testNumber, section });

    const tables = this._getTableNames(section);
    if (!tables || !tables.passages) {
      return null; // Section doesn't have passages
    }

    try {
      const { data, error } = await supabase
        .from(tables.passages)
        .select('*')
        .eq('test_number', testNumber)
        .order('passage_number', { ascending: true });

      if (error) {
        errorTracker.trackError(
          'PracticeTestsService',
          'getPassages',
          { testNumber, section },
          error
        );
        return null;
      }

      logger.info('PracticeTestsService', 'getPassages', {
        testNumber,
        section,
        count: data?.length
      });

      return data;
    } catch (error) {
      errorTracker.trackError(
        'PracticeTestsService',
        'getPassages',
        { testNumber, section },
        error
      );
      return null;
    }
  },

  /**
   * Save user's answer to a practice test question
   * @param {string} userId - User ID
   * @param {number} testNumber - Test number
   * @param {string} section - Section name
   * @param {number} questionId - Question ID from database
   * @param {number} userAnswer - User's answer (0-based index)
   * @param {boolean} isCorrect - Whether answer was correct
   * @param {number} timeSpent - Time spent in seconds
   * @returns {Promise<Object|null>} Inserted data or null on error
   */
  async savePracticeTestAnswer(
    userId,
    sessionId,
    section,
    questionId,
    userAnswer,
    correctAnswer,
    isCorrect,
    timeSpent
  ) {
    logger.debug('PracticeTestsService', 'savePracticeTestAnswer', {
      userId,
      sessionId,
      section,
      questionId,
      isCorrect,
      timeSpent
    });

    // Use upsert to prevent duplicates
    const { data, error } = await supabase.from('practice_test_results').upsert([
      {
        practice_session_id: sessionId,
        user_id: userId,
        section: section,
        question_id: questionId,
        user_answer: userAnswer,
        correct_answer: correctAnswer,
        is_correct: isCorrect,
        time_spent: timeSpent // milliseconds, not seconds
      }
    ], {
      onConflict: 'practice_session_id,question_id',
      ignoreDuplicates: false // Update if exists
    }).select(); // Return inserted data for verification

    if (error) {
      console.error(`❌ FAILED TO SAVE PRACTICE TEST ANSWER - Question ${questionId}:`, {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      errorTracker.trackError(
        'PracticeTestsService',
        'savePracticeTestAnswer',
        { userId, sessionId, section, questionId },
        error
      );
      return null;
    }

    if (!data || data.length === 0) {
      console.error(`❌ SAVE RETURNED NO DATA - Question ${questionId}`);
      return null;
    }

    logger.info('PracticeTestsService', 'savePracticeTestAnswer', {
      userId,
      sessionId,
      section,
      questionId,
      isCorrect
    });
    return data;
  },

  /**
   * Create a new practice test session
   * @param {string} userId - User ID
   * @param {number} testNumber - Test number
   * @param {string} section - Section name or 'full'
   * @param {number} totalQuestions - Total questions in section
   * @returns {Promise<Object|null>} Created session or null on error
   */
  async createPracticeTestSession(userId, testNumber, section, totalQuestions) {
    logger.debug('PracticeTestsService', 'createPracticeTestSession', {
      userId,
      testNumber,
      section,
      totalQuestions
    });

    // Store testNumber in test_name as "Practice Test X (Test #Y)" for retrieval
    const displayNumber = testNumber - 1; // 2-7 becomes 1-6
    const { data, error } = await supabase
      .from('practice_test_sessions')
      .insert([
        {
          user_id: userId,
          test_number: testNumber, // Store the actual DB test number (2-7)
          test_name: `Practice Test ${displayNumber}`,
          test_type: 'full',
          sections_included: ['english', 'math', 'reading', 'science'],
          total_questions: totalQuestions,
          time_limit_minutes: 175 // Full ACT is 175 minutes
        }
      ])
      .select();

    if (error) {
      console.error('❌ Database error creating practice test session:', {
        error,
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        attempted: { userId, testNumber, totalQuestions }
      });
      errorTracker.trackError(
        'PracticeTestsService',
        'createPracticeTestSession',
        { userId, testNumber, section },
        error
      );
      return null;
    }

    logger.info('PracticeTestsService', 'createPracticeTestSession', {
      userId,
      testNumber,
      section,
      sessionId: data[0]?.id
    });
    return data[0];
  },

  /**
   * Complete a practice test session
   * @param {string} sessionId - Session ID
   * @param {number} correctAnswers - Number of correct answers
   * @param {number} scorePercentage - Score as percentage
   * @returns {Promise<Object|null>} Updated session or null on error
   */
  async completePracticeTestSession(sessionId, correctAnswers, scorePercentage) {
    logger.debug('PracticeTestsService', 'completePracticeTestSession', {
      sessionId,
      correctAnswers,
      scorePercentage
    });

    const { data, error } = await supabase
      .from('practice_test_sessions')
      .update({
        completed_at: new Date().toISOString(),
        score_percentage: scorePercentage,
        is_completed: true
      })
      .eq('id', sessionId);

    if (error) {
      errorTracker.trackError(
        'PracticeTestsService',
        'completePracticeTestSession',
        { sessionId },
        error
      );
      return null;
    }

    logger.info('PracticeTestsService', 'completePracticeTestSession', {
      sessionId,
      scorePercentage
    });
    return data;
  },

  /**
   * Get user's practice test history for a specific test number
   * @param {string} userId - User ID
   * @param {number} testNumber - Optional test number filter
   * @returns {Promise<Array|null>} Array of sessions or null on error
   */
  async getUserPracticeTestHistory(userId, testNumber = null) {
    logger.debug('PracticeTestsService', 'getUserPracticeTestHistory', { userId, testNumber });

    let query = supabase
      .from('practice_test_sessions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (testNumber) {
      query = query.eq('test_number', testNumber);
    }

    const { data, error } = await query;

    if (error) {
      errorTracker.trackError(
        'PracticeTestsService',
        'getUserPracticeTestHistory',
        { userId, testNumber },
        error
      );
      return null;
    }

    logger.info('PracticeTestsService', 'getUserPracticeTestHistory', {
      userId,
      testNumber,
      count: data?.length
    });
    return data;
  },

  /**
   * Get user's performance by section for a specific test
   * @param {string} userId - User ID
   * @param {number} testNumber - Test number
   * @returns {Promise<Object|null>} Performance by section or null on error
   */
  async getUserPerformanceBySection(userId, testNumber) {
    logger.debug('PracticeTestsService', 'getUserPerformanceBySection', { userId, testNumber });

    const { data, error } = await supabase
      .from('practice_test_sessions')
      .select('section, score_percentage, correct_answers, total_questions')
      .eq('user_id', userId)
      .eq('test_number', testNumber)
      .eq('completed', true)
      .order('created_at', { ascending: false });

    if (error) {
      errorTracker.trackError(
        'PracticeTestsService',
        'getUserPerformanceBySection',
        { userId, testNumber },
        error
      );
      return null;
    }

    // Group by section and get the latest score for each
    const sectionPerformance = {};
    data.forEach((session) => {
      if (!sectionPerformance[session.section]) {
        sectionPerformance[session.section] = session;
      }
    });

    logger.info('PracticeTestsService', 'getUserPerformanceBySection', {
      userId,
      testNumber,
      sections: Object.keys(sectionPerformance).length
    });
    return sectionPerformance;
  },

  /**
   * Get the total count of available practice tests
   * @returns {Promise<number|null>} Count of unique test numbers or null on error
   */
  async getAvailablePracticeTestsCount() {
    logger.debug('PracticeTestsService', 'getAvailablePracticeTestsCount', {});

    // Check english questions table to get available test numbers
    const { data, error } = await supabase
      .from('practice_test_english_questions')
      .select('test_number')
      .order('test_number', { ascending: true });

    if (error) {
      errorTracker.trackError(
        'PracticeTestsService',
        'getAvailablePracticeTestsCount',
        {},
        error
      );
      return null;
    }

    // Get unique test numbers
    const uniqueTests = [...new Set(data.map((q) => q.test_number))];

    logger.info('PracticeTestsService', 'getAvailablePracticeTestsCount', {
      count: uniqueTests.length
    });
    return uniqueTests.length;
  },

  /**
   * Get section breakdown for a practice test session
   * Aggregates results by section from practice_test_results table
   * @param {string} sessionId - Practice test session ID
   * @returns {Promise<Object|null>} Section scores object {english: {correct, total}, ...}
   */
  async getSessionSectionBreakdown(sessionId) {
    logger.debug('PracticeTestsService', 'getSessionSectionBreakdown', { sessionId });

    try {
      // Fetch all results for this session
      const { data: results, error } = await supabase
        .from('practice_test_results')
        .select('section, is_correct')
        .eq('practice_session_id', sessionId);

      if (error) {
        errorTracker.trackError(
          'PracticeTestsService',
          'getSessionSectionBreakdown',
          { sessionId },
          error
        );
        return null;
      }

      // Define total questions per section
      const sectionTotals = {
        english: 75,
        math: 60,
        reading: 40,
        science: 40
      };

      // Aggregate by section
      const sectionBreakdown = {};

      results.forEach(result => {
        const section = result.section.toLowerCase();
        if (!sectionBreakdown[section]) {
          sectionBreakdown[section] = {
            correct: 0,
            total: sectionTotals[section] || 0,
            answered: 0
          };
        }

        sectionBreakdown[section].answered++;
        if (result.is_correct) {
          sectionBreakdown[section].correct++;
        }
      });

      logger.info('PracticeTestsService', 'getSessionSectionBreakdown', {
        sessionId,
        sections: Object.keys(sectionBreakdown).length
      });

      return sectionBreakdown;
    } catch (error) {
      errorTracker.trackError(
        'PracticeTestsService',
        'getSessionSectionBreakdown',
        { sessionId },
        error
      );
      return null;
    }
  }
};

export default PracticeTestsService;
